import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { apiError, ADMIN_SESSION_INVALID_EVENT } from "@/lib/api";

const AdminAuthContext = createContext(null);

const TOKEN_KEY = "admin_token";
const USER_KEY = "admin_user";
// How often an open admin portal silently re-checks (and thereby renews) its
// session/token while this tab holds a token. Cheap, and well inside the token's
// renewal window, so an actively-working admin is never cut off.
const ADMIN_RENEW_INTERVAL_MS = 10 * 60 * 1000;

function readCachedUser() {
  try {
    const raw = sessionStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Dedicated admin session, isolated from the customer session. The admin token
// lives in sessionStorage (cleared when the browser/tab closes) so opening the
// portal in a new session ALWAYS requires fresh email + password + TOTP. A
// customer session can never satisfy this context.
//
// Because sessionStorage is per tab, two tabs/devices hold two independent
// sessions: one device can never read, overwrite or clear another device's token,
// and a session is only ever dropped when the BACKEND rejects it (401/403 with an
// authentication detail). Network/5xx failures keep the session intact.
export function AdminAuthProvider({ children }) {
  const [adminUser, setAdminUser] = useState(null); // null=loading | false=none | obj
  const [ready, setReady] = useState(false);
  const [hasToken, setHasToken] = useState(false);
  // Set ONLY when the backend rejected the session (or the portal was explicitly
  // unwound because of it) — never for a transient/technical failure.
  const [sessionExpired, setSessionExpired] = useState(false);
  const [sessionMessage, setSessionMessage] = useState("");
  // Set when the session could not be confirmed for a non-auth reason (offline,
  // 5xx, CORS): the token is kept and the admin is offered a retry.
  const [verifyError, setVerifyError] = useState("");

  const markSessionExpired = useCallback((message) => {
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    } catch { /* ignore */ }
    setHasToken(false);
    setAdminUser(false);
    setSessionMessage(message || "Your admin session expired. Please sign in again.");
    setSessionExpired(true);
    setVerifyError("");
  }, []);

  const validate = useCallback(async () => {
    let token = null;
    try { token = sessionStorage.getItem(TOKEN_KEY); } catch { token = null; }
    setHasToken(!!token);
    if (!token) { setAdminUser(false); setReady(true); return { ok: false, reason: "no-token" }; }
    try {
      // /auth/admin/session re-checks role + authorized email + admin scope + this
      // device's live session id server-side.
      const { data } = await api.get("/auth/admin/session", { _adminAuth: true });
      if (data?.user?.role === "admin") {
        // The backend hands back a fresh token for this SAME live session once the
        // current one nears expiry, so an actively-working admin is not signed out
        // mid-task. Only a session the backend just confirmed live can produce one.
        if (typeof data.token === "string" && data.token) {
          try { sessionStorage.setItem(TOKEN_KEY, data.token); } catch { /* ignore */ }
          setHasToken(true);
        }
        try { sessionStorage.setItem(USER_KEY, JSON.stringify(data.user)); } catch { /* ignore */ }
        setAdminUser(data.user);
        setSessionExpired(false);
        setSessionMessage("");
        setVerifyError("");
        return { ok: true, user: data.user, session: data.session };
      }
      markSessionExpired("Admin access could not be confirmed. Please sign in again.");
      return { ok: false, reason: "not-admin" };
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        // The backend genuinely rejected this session — the only case in which the
        // portal may say "session expired".
        markSessionExpired(apiError(e));
        return { ok: false, reason: "rejected", message: apiError(e) };
      }
      // Offline / CORS / 5xx / timeout: NOT an expired session. Keep the token and
      // the cached admin identity so a temporary outage cannot log the admin out.
      const cached = readCachedUser();
      setAdminUser(cached && cached.role === "admin" ? cached : false);
      setVerifyError(apiError(e));
      return { ok: false, reason: "unreachable", message: apiError(e) };
    } finally {
      setReady(true);
    }
  }, [markSessionExpired]);

  useEffect(() => { validate(); }, [validate]);

  // Any admin API call that the backend rejects as unauthenticated marks the
  // session expired for this tab (and this tab only).
  useEffect(() => {
    const onInvalid = (ev) => markSessionExpired(ev?.detail?.message);
    window.addEventListener(ADMIN_SESSION_INVALID_EVENT, onInvalid);
    return () => window.removeEventListener(ADMIN_SESSION_INVALID_EVENT, onInvalid);
  }, [markSessionExpired]);

  // Silent renewal / early detection, ONLY while this tab actually holds an admin
  // token. It reuses validate(), which is deliberately non-destructive: a transient
  // failure keeps the token and the cached identity (a network/5xx blip can never
  // become a logout). Running it on mount (above), on a schedule, and when the tab
  // becomes visible again means an actively-working admin's session/token is renewed
  // before it lapses, and a genuinely dead session is surfaced BEFORE a save fails.
  useEffect(() => {
    if (!hasToken) return undefined;
    const tick = () => { validate(); };
    const id = setInterval(tick, ADMIN_RENEW_INTERVAL_MS);
    const onFocus = () => tick();
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [hasToken, validate]);

  const setAdminAuth = (token, user) => {
    try {
      sessionStorage.setItem(TOKEN_KEY, token);
      sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch { /* ignore */ }
    setHasToken(true);
    setAdminUser(user);
    setSessionExpired(false);
    setSessionMessage("");
    setVerifyError("");
  };

  const adminLogout = async () => {
    // Ends THIS device's session only; other devices stay signed in.
    try {
      await api.post("/auth/admin/logout", {});
    } catch (e) {
      console.debug("admin logout call failed (session cleared locally):", e?.message);
    }
    try {
      sessionStorage.removeItem(TOKEN_KEY);
      sessionStorage.removeItem(USER_KEY);
    } catch { /* ignore */ }
    setHasToken(false);
    setAdminUser(false);
    setSessionExpired(false);
    setSessionMessage("");
    setVerifyError("");
  };

  const clearSessionMessage = () => setSessionMessage("");

  return (
    <AdminAuthContext.Provider value={{
      adminUser, ready, hasToken, setAdminAuth, adminLogout,
      sessionExpired, sessionMessage, clearSessionMessage,
      verifyError, retryValidate: validate,
    }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export const useAdminAuth = () => useContext(AdminAuthContext);
